import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Users, TrendingUp, AlertTriangle, Zap, RefreshCw, Calendar } from 'lucide-react';
import { useEventStats } from '@/hooks/useEvents';
import { useAuthStore } from '@/stores/useAuthStore';
import { Card, StatCard, LeadScoreBadge, SkeletonCard, Button } from '@/components/ui';
import { useT } from '@/i18n/LanguageContext';

const BAND_COLORS: Record<string, string> = { A: '#06B6D4', B: '#3B82F6', C: '#523EF7', D: '#EF4444' };

export default function DashboardPage() {
  const t = useT();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const { data: stats, isLoading, refetch } = useEventStats(selectedEventId);

  const navigate = useNavigate();

  // No event selected
  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center max-w-sm">
          <Calendar size={32} className="mx-auto text-reevol-text-muted mb-3" />
          <h2 className="text-lg font-bold text-reevol-text mb-2">{t.common.selectEvent}</h2>
          <p className="text-sm text-reevol-text-muted mb-4">
            {t.common.chooseEvent}
          </p>
          <Button onClick={() => navigate('/events')}>
            {t.common.goToEvents}
          </Button>
        </Card>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
        <SkeletonCard />
      </div>
    );
  }

  const bandData = Object.entries(stats.bandCounts).map(([name, count]) => ({
    name,
    count,
    fill: BAND_COLORS[name] || '#ccc',
  }));

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-reevol-text">{t.dashboard.title}</h1>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-xl hover:bg-gray-100 text-reevol-text-muted hover:text-reevol-text transition-colors"
          title={t.dashboard.refresh}
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t.dashboard.totalLeads} value={stats.totalLeads} icon={<Users size={20} />} />
        <div className="glass-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-reevol-text-label uppercase tracking-widest">{t.dashboard.today}</p>
              <p className="text-[32px] font-bold text-reevol-text mt-1">{stats.todayLeads}</p>
            </div>
            <div className="text-reevol-text-muted relative">
              <Zap size={20} />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-reevol-cyan rounded-full animate-pulse-dot" />
            </div>
          </div>
        </div>
        <div className="glass-card p-5 border-l-[3px] border-l-reevol-cyan">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-reevol-text-label uppercase tracking-widest">{t.dashboard.hotLeads}</p>
              <p className="text-[32px] font-bold mt-1" style={{ color: stats.hotLeads > 0 ? '#06B6D4' : undefined }}>
                {stats.hotLeads}
              </p>
            </div>
            <div className="text-reevol-text-muted"><TrendingUp size={20} /></div>
          </div>
        </div>
        <div className="glass-card p-5 border-l-[3px] border-l-red-500">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-reevol-text-label uppercase tracking-widest">{t.dashboard.atRisk}</p>
              <p className="text-[32px] font-bold mt-1" style={{ color: stats.atRiskLeads > 0 ? '#EF4444' : undefined }}>
                {stats.atRiskLeads}
              </p>
            </div>
            <div className="text-reevol-text-muted"><AlertTriangle size={20} /></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Score Distribution */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-4">{t.dashboard.scoreDistribution}</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={bandData}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 13, fontWeight: 600, fill: 'rgba(0,0,0,0.4)' }} />
              <YAxis hide />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', fontSize: 13, background: '#ffffff', color: '#111827' }}
                formatter={(value: number) => [value, t.dashboard.tooltipLeads]}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
                {bandData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top Countries */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-4">{t.dashboard.topRegions}</h2>
          {stats.topCountries.length === 0 ? (
            <p className="text-sm text-reevol-text-muted">{t.common.noDataYet}</p>
          ) : (
            <ul className="space-y-0">
              {stats.topCountries.slice(0, 5).map((tc, i) => (
                <li key={tc.country} className={`flex items-center justify-between text-sm py-2.5 ${i < stats.topCountries.length - 1 ? 'border-b border-reevol-border' : ''}`}>
                  <span className="text-reevol-text">{tc.country || t.common.unknown}</span>
                  <span className="font-semibold text-reevol-text-secondary">{tc.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Avg Score */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-3">{t.dashboard.averageScore}</h2>
          <div className="flex items-center gap-3">
            <LeadScoreBadge
              band={stats.avgScore >= 75 ? 'A' : stats.avgScore >= 50 ? 'B' : stats.avgScore >= 25 ? 'C' : 'D'}
              score={stats.avgScore}
              size="lg"
            />
            <div>
              <p className="text-3xl font-bold text-reevol-text">{stats.avgScore}</p>
              <p className="text-xs text-reevol-text-muted">{t.common.outOf100}</p>
            </div>
          </div>
        </Card>

        {/* Check Balance */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-3">{t.dashboard.checksRemaining}</h2>
          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-3xl font-bold text-reevol-text">
              {stats.checksLimit - stats.checksUsed}
            </span>
            <span className="text-sm text-reevol-text-muted">/ {stats.checksLimit}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-reevol-blue to-reevol-cyan transition-all duration-500"
              style={{ width: `${Math.min(100, (stats.checksUsed / stats.checksLimit) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-reevol-text-muted mt-2">{stats.checksUsed} {t.common.checksUsed}</p>
        </Card>
      </div>

      {/* Quick action */}
      <button
        onClick={() => navigate('/scan')}
        className="w-full md:w-auto px-8 py-3 rounded-full bg-[#523EF7] text-white font-semibold shadow-[0_4px_16px_rgba(82,62,247,0.3)] hover:bg-[#3D2DD4] hover:-translate-y-px hover:shadow-[0_6px_24px_rgba(82,62,247,0.35)] transition-all duration-200"
      >
        {t.dashboard.newLeadCheck}
      </button>
    </div>
  );
}
